import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/layout/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword, session } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  // Supabase establishes a session from the invite/recovery link in the URL.
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Use at least 8 characters.");
    if (pw !== confirm) return setErr("Passwords do not match.");
    setErr("");
    setBusy(true);
    const { error } = await updatePassword(pw);
    setBusy(false);
    if (error) return setErr(error.message || "Could not update the password. The link may have expired.");
    toast.success("Password updated", { description: "You can now sign in with your new password." });
    navigate("/");
  };

  const noSession = ready && !session;

  return (
    <AuthLayout title="Set a new password" subtitle="Choose a strong password for your account.">
      {noSession && (
        <p className="mb-4 flex items-center gap-1.5 rounded-md bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive" data-testid="no-session">
          <AlertCircle className="h-4 w-4 shrink-0" /> This link is invalid or has expired. Please request a new reset link.
        </p>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-1.5">
          <Label htmlFor="pw">New password</Label>
          <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password" autoComplete="new-password" />
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-accent" /> Minimum 8 characters.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password" autoComplete="new-password" />
        </div>
        {err && <p className="text-xs font-medium text-destructive" data-testid="reset-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </AuthLayout>
  );
}
